import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

export const AmendmentReviewPanel = ({ order, onDone }) => {
  const [note, setNote] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const pending = (order.amendments || []).find((a) => a.status === "PENDING");
  const history = (order.amendments || []).filter((a) => a.status !== "PENDING");
  if (!pending && !history.length && !(order.payment_adjustments || []).length) return null;
  const paidQty = pending?.changes?.lines?.length && ["PAYMENT_PROCESSING", "PAID", "CONFIRMED", "BALANCE_DUE"].includes(order.status);

  const act = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/amendments/${pending.id}/${kind}`, { note, acknowledge_payment_adjustment: ack });
      toast.success(kind === "approve" ? "Change approved and applied" : "Change request rejected");
      setNote(""); setAck(false);
      onDone?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Card className="border-terracotta/40 bg-white" data-testid="amendment-review-panel">
      <CardContent className="p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Order change requests</p>
        {pending && (
          <div className="mt-3 rounded-md border border-border bg-background p-4" data-testid="pending-amendment">
            <p className="text-sm font-medium text-charcoal">Requested {new Date(pending.requested_at).toLocaleString()} · {pending.window === "LATE" ? "after the 24h window (not guaranteed)" : "within 24h window"}</p>
            <ul className="mt-2 text-sm text-charcoal">
              {pending.changes.lines?.map((l) => <li key={l.line_id}>{l.sku} {l.variant_label}: {l.from_boxes} → <b>{l.to_boxes}</b> boxes</li>)}
              {pending.changes.delivery_instructions != null && <li>Delivery instructions: “{pending.changes.delivery_instructions}”</li>}
              {pending.changes.ship_to && <li>Ship-to destination change</li>}
            </ul>
            {pending.reason && <p className="mt-1 text-xs text-muted-foreground">Reason: {pending.reason}</p>}
            <p className="mt-2 text-xs text-muted-foreground">Approval re-validates Florida availability and allocations atomically.</p>
            {paidQty && (
              <label className="mt-2 flex items-start gap-2 rounded-md border border-terracotta/40 p-3 text-xs text-charcoal" data-testid="amend-payment-ack-row">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="amend-payment-ack" className="mt-0.5" />
                This order is paid. Approving records a payment adjustment (refund or additional charge) that must be settled explicitly — the paid amount is never changed silently.
              </label>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Input className="h-9 flex-1 bg-white" placeholder="Decision note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="amend-note" />
              <Button size="sm" disabled={busy || (paidQty && !ack)} onClick={() => act("approve")} data-testid="amend-approve" className="bg-terracotta text-white hover:bg-terracotta-hover">Approve</Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => act("reject")} data-testid="amend-reject">Reject</Button>
            </div>
          </div>
        )}
        {history.length > 0 && (
          <ul className="mt-3 space-y-1 text-xs text-muted-foreground" data-testid="amendment-admin-history">
            {history.map((a) => <li key={a.id}>{new Date(a.resolved_at).toLocaleString()} · {a.status} · {a.changes.lines?.map((l) => `${l.sku} ${l.from_boxes}→${l.to_boxes}`).join(", ")}{a.changes.delivery_instructions != null ? " · instructions" : ""}{a.resolution_note ? ` — ${a.resolution_note}` : ""}</li>)}
          </ul>
        )}
        {(order.payment_adjustments || []).length > 0 && (
          <ul className="mt-3 space-y-1 text-xs" data-testid="payment-adjustments">
            {order.payment_adjustments.map((p, i) => <li key={i} className="text-terracotta">Payment adjustment {p.delta_usd < 0 ? "refund" : "charge"} ${Math.abs(p.delta_usd).toFixed(2)} · {p.status}</li>)}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
